import type { FC } from 'react';
import { Button, Card, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { Iconify, Label } from '@pflegenavi/web-components';
import { useBankAccountStatus } from '../../hooks/useBankAccountStatus';

export const BankAccountCard: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isReady, recipientPending, scaUrl, openScaUrl } =
    useBankAccountStatus();

  const handleSetupBankAccount = () => {
    navigate(spAppMainPages.SETUP_BANK_ACCOUNT);
  };

  const canResumeSca = recipientPending && Boolean(scaUrl);

  return (
    <Card sx={{ p: 2 }}>
      <Stack gap={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Iconify icon="mdi:bank" width={24} height={24} />
            <Typography variant="subtitle1">
              {t('service-provider-app.profile.bank-account')}
            </Typography>
          </Stack>
          {isReady ? (
            <Label color="success">
              {t('service-provider-app.profile.configured')}
            </Label>
          ) : (
            <Label color="warning">
              {recipientPending
                ? t('service-provider-app.profile.verification-pending')
                : t('service-provider-app.profile.not-configured')}
            </Label>
          )}
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {isReady
            ? t(
                'service-provider-app.profile.bank-account-configured-description'
              )
            : recipientPending
            ? t(
                'service-provider-app.profile.bank-account-verification-pending-description'
              )
            : t(
                'service-provider-app.profile.bank-account-not-configured-description'
              )}
        </Typography>

        <Button
          variant={isReady ? 'outlined' : 'contained'}
          onClick={canResumeSca ? openScaUrl : handleSetupBankAccount}
          startIcon={
            <Iconify
              icon={
                canResumeSca
                  ? 'mdi:shield-check-outline'
                  : isReady
                  ? 'mdi:pencil'
                  : 'mdi:plus'
              }
            />
          }
          fullWidth
        >
          {canResumeSca
            ? t(
                'service-provider-app.dashboard.resume-bank-account-verification-button'
              )
            : isReady
            ? t('service-provider-app.profile.update-bank-account')
            : t('service-provider-app.profile.setup-bank-account')}
        </Button>
      </Stack>
    </Card>
  );
};
